// CuratedPanel — reseller-side surface for the curated-purchase
// service (migration 0053). Lives under "Закупки" in My Store.
//
// Three modes:
//   list   — table of own requests with status pills
//   form   — submit a new request (account_access or product_links)
//   detail — read-only view of a single request, with invoice details
//            when status is 'invoiced', and a cancel button when
//            status is 'submitted' or 'priced'.
//
// State of the world is mirrored from /me/curated; we re-fetch on
// every modal close so newly issued invoices (or expired ones) show
// up without a manual reload.

import { useState, useEffect, useMemo } from "react";
import { api } from "../api.js";
import { T_NEW } from "../theme.js";
import { Btn, Inp, Mono, Serif } from "./ui.jsx";
import { useToast } from "./toast.jsx";
import { useT, plural } from "../i18n.jsx";

// Status colors are static; labels come from i18n at render time.
const STATUS_TONES = {
  submitted: T_NEW.ink56,
  priced:    T_NEW.accent,
  purchased: T_NEW.accent,
  invoiced:  T_NEW.persimmon,
  paid:      T_NEW.success,
  completed: T_NEW.success,
  cancelled: T_NEW.ink38,
  rejected:  T_NEW.danger,
};

const usd = (cents) => `$${(cents / 100).toFixed(2)}`;

export function CuratedPanel({ user }) {
  const toast = useToast();
  const t = useT();
  const [view, setView] = useState("list");
  const [activeId, setActiveId] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { setRows(await api.listCurated()); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (view === "form") return (
    <NewRequestForm onCancel={() => setView("list")}
                    onSubmitted={async (id) => {
                      await load();
                      setActiveId(id);
                      setView("detail");
                    }} />
  );
  if (view === "detail" && activeId) return (
    <DetailView id={activeId} onBack={() => { setView("list"); load(); }} />
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                    marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div>
          <Serif size={20} color={T_NEW.ink} style={{ fontWeight: 600 }}>
            {t("admin.curated.reseller.title")}
          </Serif>
          <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink56, marginTop: 2,
                        fontFamily: T_NEW.fontSerif, fontStyle: "italic" }}>
            {t("admin.curated.reseller.subtitle")}
          </div>
        </div>
        <Btn variant="primary" onClick={() => setView("form")}>{t("admin.curated.reseller.new")}</Btn>
      </div>

      {loading
        ? <div style={{ padding: 30, textAlign: "center", color: T_NEW.ink56 }}>{t("admin.curated.reseller.loading")}</div>
        : rows.length === 0
          ? <div style={{ padding: 40, textAlign: "center",
                          background: T_NEW.paperSoft, borderRadius: T_NEW.r2,
                          border: `1px dashed ${T_NEW.ink12}` }}>
              <Serif size={14} color={T_NEW.ink56}>
                {t("admin.curated.reseller.empty")}
              </Serif>
            </div>
          : rows.map((r) => {
              const tone = STATUS_TONES[r.status] || T_NEW.ink;
              const statusKey = `admin.curated.reseller.status.${r.status}`;
              return (
                <button key={r.id}
                        type="button"
                        onClick={() => { setActiveId(r.id); setView("detail"); }}
                        style={{ display: "block", width: "100%", textAlign: "left",
                                 padding: 14, marginBottom: 10,
                                 background: T_NEW.paperRaised,
                                 border: `1px solid ${T_NEW.ink12}`,
                                 borderRadius: T_NEW.r2,
                                 cursor: "pointer",
                                 fontFamily: T_NEW.fontSans }}>
                  <div style={{ display: "flex", justifyContent: "space-between",
                                alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <Mono size={13} style={{ fontWeight: 600 }}>
                      #{r.id.slice(0, 8)} · {r.requestType === "account_access"
                        ? t("admin.curated.reseller.row.account")
                        : t("admin.curated.reseller.row.links")}
                    </Mono>
                    <span style={{ fontSize: 12, color: tone, fontWeight: 600 }}>
                      {t(statusKey)}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: T_NEW.ink56, marginTop: 6 }}>
                    {r.itemCount} {t(plural(r.itemCount, "admin.curated.reseller.row.items"))} · {usd(r.statedTotalCents)} · {r.preferredCurrency}
                  </div>
                </button>
              );
            })
      }
    </div>
  );
}

// ─── New request form ────────────────────────────────────────────────
function NewRequestForm({ onCancel, onSubmitted }) {
  const toast = useToast();
  const t = useT();
  const [type, setType] = useState("product_links");
  const [siteUrl, setSiteUrl] = useState("");
  const [accountLogin, setAccountLogin] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [links, setLinks] = useState([{ url: "", qty: 1, notes: "" }]);
  const [deliveryName, setDeliveryName] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [itemCount, setItemCount] = useState(1);
  const [statedTotal, setStatedTotal] = useState(""); // displayed USD
  const [currency, setCurrency] = useState("USDT");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const addLink = () => setLinks((l) => [...l, { url: "", qty: 1, notes: "" }]);
  const removeLink = (i) => setLinks((l) => l.filter((_, idx) => idx !== i));
  const updateLink = (i, k, v) => setLinks((l) =>
    l.map((row, idx) => idx === i ? { ...row, [k]: v } : row));

  const submit = async () => {
    setSubmitting(true);
    try {
      const totalCents = Math.round(parseFloat(statedTotal) * 100);
      if (!totalCents || totalCents < 1) {
        throw new Error(t("admin.curated.reseller.error.amount"));
      }
      const payload = {
        requestType: type,
        deliveryName: deliveryName.trim(),
        deliveryAddress: deliveryAddress.trim(),
        itemCount: parseInt(itemCount, 10) || 0,
        statedTotalCents: totalCents,
        preferredCurrency: currency,
        resellerNotes: notes.trim() || undefined,
      };
      if (type === "account_access") {
        payload.siteUrl = siteUrl.trim();
        payload.accountLogin = accountLogin;
        payload.accountPassword = accountPassword;
      } else {
        payload.productLinks = links
          .map((l) => ({ url: l.url.trim(),
                         qty: parseInt(l.qty, 10) || 1,
                         notes: l.notes.trim() || undefined }))
          .filter((l) => l.url.length > 0);
        if (payload.productLinks.length === 0) {
          throw new Error(t("admin.curated.reseller.error.noLinks"));
        }
      }
      const r = await api.submitCurated(payload);
      toast.success(t("admin.curated.reseller.created.ok"));
      onSubmitted(r.id);
    } catch (e) { toast.error(e.message); }
    setSubmitting(false);
  };

  return (
    <div>
      <button type="button" onClick={onCancel}
              style={{ background: "none", border: 0, color: T_NEW.ink56,
                       cursor: "pointer", marginBottom: 14, fontSize: 13 }}>
        {t("admin.curated.reseller.back")}
      </button>

      <Serif size={22} color={T_NEW.ink} style={{ fontWeight: 600, marginBottom: 14 }}>
        {t("admin.curated.reseller.new.title")}
      </Serif>

      <Mono size={T_NEW.micro} color={T_NEW.ink56}
            style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
        {t("admin.curated.reseller.mode.label")}
      </Mono>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
        {[
          { v: "product_links",  titleKey: "admin.curated.reseller.mode.links.title",
            descKey: "admin.curated.reseller.mode.links.desc" },
          { v: "account_access", titleKey: "admin.curated.reseller.mode.account.title",
            descKey: "admin.curated.reseller.mode.account.desc" },
        ].map((o) => (
          <button key={o.v} type="button" onClick={() => setType(o.v)}
                  style={{ textAlign: "left", padding: 14,
                           border: `2px solid ${type === o.v ? T_NEW.accent : T_NEW.ink12}`,
                           background: type === o.v ? `${T_NEW.accent}10` : T_NEW.paper,
                           borderRadius: T_NEW.r2, cursor: "pointer",
                           fontFamily: T_NEW.fontSans }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{t(o.titleKey)}</div>
            <div style={{ fontSize: 12, color: T_NEW.ink56,
                          fontFamily: T_NEW.fontSerif, fontStyle: "italic",
                          lineHeight: 1.4 }}>{t(o.descKey)}</div>
          </button>
        ))}
      </div>

      {type === "account_access" && (
        <>
          <Inp label={t("admin.curated.reseller.account.site")} value={siteUrl} onChange={setSiteUrl}
               placeholder="https://amazon.com" inputMode="url" autoComplete="off" />
          <Inp label={t("admin.curated.reseller.account.login")} value={accountLogin} onChange={setAccountLogin}
               placeholder="user@example.com" autoComplete="off" />
          <Inp label={t("admin.curated.reseller.account.password")} value={accountPassword} onChange={setAccountPassword}
               placeholder="•••••••••" autoComplete="off" type="password" />
          <div style={{ fontSize: 12, color: T_NEW.ink56,
                        fontFamily: T_NEW.fontSerif, fontStyle: "italic",
                        marginBottom: 16, lineHeight: 1.5,
                        padding: 10, background: `${T_NEW.persimmon}10`,
                        border: `1px solid ${T_NEW.persimmon}33`,
                        borderRadius: T_NEW.r1 }}>
            {t("admin.curated.reseller.account.hint")}
          </div>
        </>
      )}

      {type === "product_links" && (
        <>
          <Mono size={T_NEW.micro} color={T_NEW.ink56}
                style={{ display: "block", marginBottom: 6 }}>
            {t("admin.curated.reseller.links.label")}
          </Mono>
          {links.map((l, i) => (
            <div key={i} style={{ display: "grid",
                                   gridTemplateColumns: "1fr 80px auto",
                                   gap: 8, marginBottom: 8, alignItems: "start" }}>
              <Inp value={l.url} onChange={(v) => updateLink(i, "url", v)}
                   placeholder="https://…" />
              <Inp value={l.qty} onChange={(v) => updateLink(i, "qty", v)}
                   placeholder={t("admin.curated.reseller.links.qty")} inputMode="numeric" />
              {links.length > 1 && (
                <button type="button" onClick={() => removeLink(i)}
                        style={{ background: "none", border: 0, color: T_NEW.danger,
                                 cursor: "pointer", padding: "12px 6px" }}>×</button>
              )}
            </div>
          ))}
          <Btn variant="ghost" onClick={addLink}>{t("admin.curated.reseller.links.add")}</Btn>
          <div style={{ height: 14 }} />
        </>
      )}

      <Inp label={t("admin.curated.reseller.delivery.name")} value={deliveryName} onChange={setDeliveryName}
           placeholder={t("admin.curated.reseller.delivery.name.placeholder")} autoComplete="off" />
      <Inp label={t("admin.curated.reseller.delivery.address")} value={deliveryAddress} onChange={setDeliveryAddress}
           placeholder={t("admin.curated.reseller.delivery.address.placeholder")} autoComplete="off" />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Inp label={t("admin.curated.reseller.itemCount")} value={itemCount} onChange={setItemCount}
             placeholder="1" inputMode="numeric" />
        <Inp label={t("admin.curated.reseller.statedTotal")} value={statedTotal} onChange={setStatedTotal}
             placeholder="120.00" inputMode="decimal" />
      </div>

      <Mono size={T_NEW.micro} color={T_NEW.ink56}
            style={{ display: "block", marginBottom: 6 }}>
        {t("admin.curated.reseller.currency")}
      </Mono>
      <select value={currency} onChange={(e) => setCurrency(e.target.value)}
              style={{ width: "100%", marginBottom: 14,
                       background: T_NEW.paper, color: T_NEW.ink,
                       border: `1px solid ${T_NEW.ink12}`,
                       padding: "12px 14px", borderRadius: T_NEW.r1,
                       fontSize: 15, fontFamily: T_NEW.fontSans }}>
        <option value="USDT">USDT</option>
        <option value="USDC">USDC</option>
        <option value="RUB">{t("admin.curated.reseller.currency.rub")}</option>
      </select>

      <Mono size={T_NEW.micro} color={T_NEW.ink56}
            style={{ display: "block", marginBottom: 6 }}>
        {t("admin.curated.reseller.notes")}
      </Mono>
      <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                rows={3} placeholder={t("admin.curated.reseller.notes.placeholder")}
                style={{ width: "100%", marginBottom: 14,
                         background: T_NEW.paper, color: T_NEW.ink,
                         border: `1px solid ${T_NEW.ink12}`,
                         padding: "12px 14px", borderRadius: T_NEW.r1,
                         fontSize: 14, fontFamily: T_NEW.fontSans,
                         resize: "vertical" }} />

      <Btn variant="primary" disabled={submitting} onClick={submit}>
        {submitting ? t("admin.curated.reseller.submitting") : t("admin.curated.reseller.submit")}
      </Btn>
      <Btn variant="ghost" onClick={onCancel} style={{ marginLeft: 8 }}>{t("admin.curated.reseller.cancel")}</Btn>
    </div>
  );
}

// ─── Detail view ─────────────────────────────────────────────────────
function DetailView({ id, onBack }) {
  const toast = useToast();
  const t = useT();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0); // re-render for countdown

  const load = async () => {
    setLoading(true);
    try { setR(await api.getCurated(id)); }
    catch (e) { toast.error(e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [id]);

  // 1Hz tick for invoice countdown; only spinning while invoice
  // is active and unexpired (so we don't burn CPU on a list view).
  useEffect(() => {
    if (!r || r.status !== "invoiced" || r.invoiceExpired) return;
    const h = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(h);
  }, [r?.status, r?.invoiceExpired]);

  const minutesLeft = useMemo(() => {
    if (!r?.invoiceExpiresAt) return null;
    const ms = r.invoiceExpiresAt * 1000 - Date.now();
    if (ms <= 0) return 0;
    return Math.ceil(ms / 60000);
  }, [r?.invoiceExpiresAt, tick]);

  if (loading || !r) return (
    <div style={{ padding: 30, textAlign: "center", color: T_NEW.ink56 }}>{t("admin.curated.reseller.loading")}</div>
  );

  const tone = STATUS_TONES[r.status] || T_NEW.ink;
  const canCancel = r.status === "submitted" || r.status === "priced";

  return (
    <div>
      <button type="button" onClick={onBack}
              style={{ background: "none", border: 0, color: T_NEW.ink56,
                       cursor: "pointer", marginBottom: 14, fontSize: 13 }}>
        {t("admin.curated.reseller.back")}
      </button>

      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <Mono size={14} color={T_NEW.ink56}>#{r.id.slice(0, 8)}</Mono>
        <span style={{ fontSize: 13, color: tone, fontWeight: 600 }}>
          {t(`admin.curated.reseller.status.${r.status}`)}
        </span>
      </div>

      {r.status === "invoiced" && !r.invoiceExpired && (
        <InvoiceCard r={r} minutesLeft={minutesLeft} onRefresh={load} />
      )}
      {r.status === "invoiced" && r.invoiceExpired && (
        <div style={{ padding: 14, background: `${T_NEW.danger}10`,
                      border: `1px solid ${T_NEW.danger}44`,
                      borderRadius: T_NEW.r2, marginBottom: 14 }}>
          <Mono size={14} color={T_NEW.ink} style={{ fontWeight: 600 }}>
            {t("admin.curated.reseller.detail.expired")}
          </Mono>
          <div style={{ fontSize: 13, color: T_NEW.ink56, marginTop: 4,
                        fontFamily: T_NEW.fontSerif, fontStyle: "italic" }}>
            {t("admin.curated.reseller.detail.expiredHint")}
          </div>
        </div>
      )}
      {r.status === "paid" && (
        <div style={{ padding: 14, background: `${T_NEW.success}10`,
                      border: `1px solid ${T_NEW.success}44`,
                      borderRadius: T_NEW.r2, marginBottom: 14 }}>
          <Mono size={14} color={T_NEW.ink} style={{ fontWeight: 600 }}>
            {t("admin.curated.reseller.detail.paid")}
          </Mono>
          <div style={{ fontSize: 13, color: T_NEW.ink56, marginTop: 4 }}>
            {t("admin.curated.reseller.detail.paidHint")}
          </div>
        </div>
      )}

      <DefList>
        <DefRow k={t("admin.curated.reseller.field.type")} v={r.requestType === "account_access"
          ? t("admin.curated.reseller.row.account") : t("admin.curated.reseller.row.links")} />
        {r.siteUrl && <DefRow k={t("admin.curated.reseller.field.site")} v={r.siteUrl} link />}
        {Array.isArray(r.productLinks) && r.productLinks.length > 0 && (
          <DefRow k={t("admin.curated.reseller.field.products")} v={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {r.productLinks.map((l, i) =>
                <li key={i}><a href={l.url} target="_blank" rel="noopener noreferrer"
                               style={{ color: T_NEW.accent }}>{l.url}</a> × {l.qty}</li>,
              )}
            </ul>} />
        )}
        <DefRow k={t("admin.curated.reseller.field.recipient")} v={r.deliveryName} />
        <DefRow k={t("admin.curated.reseller.field.address")} v={r.deliveryAddress} />
        <DefRow k={t("admin.curated.reseller.field.itemCount")} v={r.itemCount} />
        <DefRow k={t("admin.curated.reseller.field.statedTotal")} v={usd(r.statedTotalCents)} />
        {r.finalTotalCents != null && (
          <DefRow k={t("admin.curated.reseller.field.finalTotal")} v={
            <span style={{ fontWeight: r.finalTotalCents !== r.statedTotalCents ? 700 : 400,
                           color: r.finalTotalCents !== r.statedTotalCents ? T_NEW.persimmon : T_NEW.ink }}>
              {usd(r.finalTotalCents)}
            </span>
          } />
        )}
        <DefRow k={t("admin.curated.reseller.field.currency")} v={r.preferredCurrency} />
        {r.resellerNotes && <DefRow k={t("admin.curated.reseller.field.notes")} v={r.resellerNotes} />}
        {r.managerNotes && <DefRow k={t("admin.curated.reseller.field.managerNotes")} v={r.managerNotes} />}
        {r.rejectionReason && <DefRow k={t("admin.curated.reseller.field.rejectionReason")} v={r.rejectionReason} />}
        {r.receiptImageUrl && <DefRow k={t("admin.curated.reseller.field.receipt")} v={
          <a href={r.receiptImageUrl} target="_blank" rel="noopener noreferrer"
             style={{ color: T_NEW.accent }}>{t("admin.curated.reseller.receipt.open")}</a>
        } />}
      </DefList>

      {canCancel && (
        <div style={{ marginTop: 20 }}>
          <Btn variant="ghost"
               onClick={async () => {
                 if (!confirm(t("admin.curated.reseller.cancel.confirm"))) return;
                 try {
                   await api.cancelCurated(id);
                   toast.success(t("admin.curated.reseller.cancel.ok"));
                   onBack();
                 } catch (e) { toast.error(e.message); }
               }}
               style={{ color: T_NEW.danger }}>
            {t("admin.curated.reseller.cancelBtn")}
          </Btn>
        </div>
      )}
    </div>
  );
}

function InvoiceCard({ r, minutesLeft, onRefresh }) {
  const t = useT();
  const isCrypto = r.preferredCurrency !== "RUB";
  const amtStr = r.preferredCurrency === "RUB"
    ? `${parseFloat(r.invoiceAmount).toFixed(2)} ₽`
    : `${parseFloat(r.invoiceAmount).toFixed(2)} ${r.preferredCurrency}`;
  return (
    <div style={{ padding: 16, background: T_NEW.paperRaised,
                  border: `2px solid ${T_NEW.accent}`,
                  borderRadius: T_NEW.r2, marginBottom: 14 }}>
      <Mono size={T_NEW.micro} color={T_NEW.ink56}
            style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
        {t("admin.curated.reseller.invoice.title")}
      </Mono>
      <Serif size={28} color={T_NEW.ink} style={{ fontWeight: 600, marginBottom: 8 }}>
        {amtStr}
      </Serif>
      {!isCrypto && minutesLeft != null && (
        <div style={{ fontSize: 13, color: T_NEW.persimmon, fontWeight: 600, marginBottom: 12 }}>
          {t("admin.curated.reseller.invoice.minutesLeft", { n: minutesLeft })}
        </div>
      )}
      {isCrypto && (
        <div style={{ marginBottom: 12 }}>
          <Mono size={11} color={T_NEW.ink56}>
            {t("admin.curated.reseller.invoice.cryptoAddress", { currency: r.preferredCurrency })}
          </Mono>
          <div style={{ padding: 10, background: T_NEW.paperSoft,
                        borderRadius: T_NEW.r1, marginTop: 4,
                        fontFamily: T_NEW.fontMono, fontSize: 13,
                        wordBreak: "break-all" }}>
            {r.invoicePaymentTarget}
          </div>
        </div>
      )}
      {!isCrypto && (
        <a href={r.invoicePaymentTarget} target="_blank" rel="noopener noreferrer"
           style={{ display: "inline-block", padding: "12px 18px",
                    background: T_NEW.accent, color: T_NEW.paper,
                    borderRadius: T_NEW.r1, textDecoration: "none",
                    fontWeight: 600, fontSize: 14 }}>
          {t("admin.curated.reseller.invoice.payCta")}
        </a>
      )}
      <div style={{ marginTop: 12 }}>
        <Btn variant="ghost" onClick={onRefresh}>{t("admin.curated.reseller.invoice.refresh")}</Btn>
      </div>
    </div>
  );
}

// ─── Tiny helpers ────────────────────────────────────────────────────
function DefList({ children }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "160px 1fr",
                  gap: "10px 14px", fontSize: 14, lineHeight: 1.55 }}>
      {children}
    </div>
  );
}
function DefRow({ k, v, link }) {
  return (
    <>
      <div style={{ color: T_NEW.ink56, fontFamily: T_NEW.fontMono, fontSize: 12 }}>{k}</div>
      <div style={{ color: T_NEW.ink, wordBreak: "break-word" }}>
        {link && typeof v === "string" && /^https?:\/\//.test(v)
          ? <a href={v} target="_blank" rel="noopener noreferrer"
               style={{ color: T_NEW.accent }}>{v}</a>
          : v}
      </div>
    </>
  );
}
